<template>
  <b-modal
    id="template_field_types_modal"
    @show="onShow"
    @hide="onClose"
    @hidden="resetDataModal"
    centered
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    scrollable
    size="lg"
  >
    <p class="h3 text-capitalize">
      {{ $t("risk.template.field_types") }}
    </p>
    <hr />
    <div class="p-1">
      <div class="row row-cols-2 align-self-center containerSelect">
        <div
          v-for="fType in activeFieldTypes"
          :key="fType.type"
          class="col p-1 mx-1"
          :class="{ active: fieldType === fType.type }"
        >
          <label :for="fType.type" class="d-flex align-items-center column">
            <input
              :id="fType.type"
              type="radio"
              v-model="fieldType"
              :value="fType.type"
              class="fTypeSelector"
            />

            <!-- icon: svg local or feather -->
            <template v-if="isSvgIcon(fType.icon)">
              <img
                :src="require(`@/assets/images/icons/template-builder/${fType.icon}`)"
                alt="Icono"
                class="iconStyle"
              />
            </template>
            <template v-else>
              <feather-icon :icon="fType.icon || 'SquareIcon'" size="20" class="mr-1" />
            </template>

            <span>{{ $t(fType.name) }}</span>
          </label>
        </div>
      </div>
    </div>

    <div>
      <template v-if="fieldTypeConfig">
        <hr />
        <div>{{ $t("risk.template.field_type_config") }}</div>
        <div class="pt-2 px-2">
          <field-type-config
            :key="'cfg-'+fieldType"
            :field-type-cfg="localFieldTypeCfg"
            :config="fieldTypeConfig"
            @update:data="onDataUpdated"
          />
        </div>
      </template>
    </div>

    <template #modal-footer="{ close }">
      <b-button variant="danger" size="sm" @click="close()">
        {{ $t("close") }}
      </b-button>
      <b-button variant="primary" size="sm" @click="onApply(close)">
        {{ $t("actions.save") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { BModal } from "bootstrap-vue";
import { ref, watch } from "@vue/composition-api";
import FieldTypeConfig from "./FieldTypeConfig";
import { fieldTypes } from "./config";

export default {
  name: "TemplateFieldTypesModal",
  components: { BModal, FieldTypeConfig },
  props: {
    modalName: String,
    type: String,
    fieldTypeCfg: Object,
  },
  setup(props, { emit }) {
    const fieldType = ref(props.type || "");
    const fieldTypeConfig = ref(null);
    const localFieldTypeCfg = ref({ data: [] });
    const activeFieldTypes = fieldTypes;

    // Normalize to { data: [...] }
    const normalizeCfg = (src) => {
      if (Array.isArray(src)) return { data: src };
      if (src && Array.isArray(src.data)) return { data: src.data };
      if (src && typeof src.data === "string") {
        try { return { data: JSON.parse(src.data) || [] }; } catch { return { data: [] }; }
      }
      if (src && Array.isArray(src.fieldTypeCfg)) return { data: src.fieldTypeCfg };
      return { data: [] };
    };

    const setTypeConfig = (t) => {
      fieldTypeConfig.value = fieldTypes.find((f) => f.type === t) || null;
    };

    const onShow = () => {
      fieldType.value = props.type || "";
      localFieldTypeCfg.value = normalizeCfg(props.fieldTypeCfg || {});
      setTypeConfig(fieldType.value);
    };

    const onClose = () => {};
    const resetDataModal = () => {};

    //Anti-loop guard (shallow equal de {data:[...]} )
    const shallowEqualData = (a, b) => {
      const A = (a && a.data) || [];
      const B = (b && b.data) || [];
      if (A.length !== B.length) return false;
      for (let i = 0; i < A.length; i++) {
        const x = A[i], y = B[i];
        if (x === y) continue;
        if (typeof x !== "object" || typeof y !== "object") return false;
        const kx = Object.keys(x), ky = Object.keys(y);
        if (kx.length !== ky.length) return false;
        for (const k of kx) if (x[k] !== y[k]) return false;
      }
      return true;
    };

    const onDataUpdated = (payload) => {
      const next = normalizeCfg(payload);
      if (shallowEqualData(next, localFieldTypeCfg.value)) return;
      localFieldTypeCfg.value = next;
    };

    const onApply = (close) => {
      emit("selected");
      emit("update:type", fieldType.value);
      emit("update:fieldTypeCfg", {
        ...localFieldTypeCfg.value,
        data: [...(localFieldTypeCfg.value.data || [])],
      });
      close();
    };

    const isSvgIcon = (icon) => typeof icon === "string" && /\.svg$/i.test(icon);

    //Change of type boot local options
    watch(fieldType, (val, oldVal) => {
      setTypeConfig(val);
      if (oldVal && val !== oldVal) {
        localFieldTypeCfg.value = { data: [] };
      }
    });

    return {
      onShow,
      onClose,
      resetDataModal,
      onDataUpdated,
      onApply,
      fieldType,
      activeFieldTypes,
      fieldTypeConfig,
      localFieldTypeCfg,
      isSvgIcon,
    };
  },
};
</script>

<style lang="scss" scoped>
.fTypeSelector { appearance: none; padding: 0.4rem; }
.iconStyle { width: 30px; height: 30px; margin-right: 5px; }
.containerSelect :hover { background-color: #cce8ff; color: #1b3d5e; border-radius: 5px; }
.active { background-color: #cce8ff; color: #1b3d5e; border-radius: 5px; }

.dark-layout {
  color: white;
  .containerSelect :hover { background-color: #cce8ff; color: #1b3d5e; }
  .active span { color: #1b3d5e; }
}
</style>
